import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, UserPlus, Car, AtSign, Phone, Mail, Lock, User, FileText, CreditCard, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Auth() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register, loginWithToken } = useAuth();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [role, setRole] = useState(searchParams.get('role') || 'rider');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  
  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Enter details, 2: Enter OTP, 3: Reset password
  const [forgotPasswordData, setForgotPasswordData] = useState({
    identifier: '', // username or phone
    email: '',
    otp: '',
    new_password: '',
    confirm_password: '',
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    vehicle_type: '',
    vehicle_plate: '',
    license_number: '',
    agreed_to_terms: false, // Terms & Conditions checkbox
  });

  // Handle Google OAuth callback (session_id in URL hash)
  const handleGoogleCallback = async (sessionId) => {
    try {
      setSocialLoading('google');
      console.log('Processing Google callback with session:', sessionId);
      
      // Use our backend proxy to avoid CORS issues
      const response = await axios.get(
        `${API}/auth/oauth/session-data?session_id=${sessionId}`
      );
      
      console.log('OAuth session data received:', response.data);
      const { email, name, picture, session_token } = response.data;
      
      if (!email) {
        throw new Error('No email received from OAuth provider');
      }
      
      // Send to our backend to create/login user
      const backendResponse = await axios.post(`${API}/auth/social/google`, {
        email,
        name,
        picture,
        session_token,
      });
      
      console.log('Backend response:', backendResponse.data);
      
      if (backendResponse.data.token) {
        loginWithToken(backendResponse.data.token, backendResponse.data.user);
        toast.success('Welcome to vgoride!');
        
        // Check if profile needs completion (for new social login users)
        if (backendResponse.data.requires_profile_completion || backendResponse.data.user.profile_complete === false) {
          navigate('/complete-profile');
        } else {
          navigate(`/${backendResponse.data.user.role}`);
        }
      }
    } catch (error) {
      console.error('Google auth error:', error);
      // More detailed error message
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      console.error('Error details:', errorMsg);
      toast.error('Google sign-in failed. Please try again.');
    } finally {
      setSocialLoading(null);
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      console.log('Found session_id in URL hash:', sessionId);
      if (sessionId) {
        handleGoogleCallback(sessionId);
      }
    }
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Sign-In
  const handleGoogleSignIn = () => {
    setSocialLoading('google');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Facebook Sign-In (placeholder - requires Facebook App ID)
  const handleFacebookSignIn = () => {
    toast.info('Facebook Sign-In requires Facebook App configuration. Please contact support.');
  };

  // Apple Sign-In (placeholder - requires Apple Developer account)
  const handleAppleSignIn = () => {
    toast.info('Apple Sign-In requires Apple Developer configuration. Please contact support.');
  };

  useEffect(() => {
    const mode = searchParams.get('mode');
    const urlRole = searchParams.get('role');
    if (mode) setIsLogin(mode === 'login');
    if (urlRole) setRole(urlRole);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(`/${user.role}`);
      } else {
        // Validate all required fields
        if (!formData.first_name.trim()) {
          toast.error('First name is required');
          setLoading(false);
          return;
        }
        if (!formData.last_name.trim()) {
          toast.error('Last name is required');
          setLoading(false);
          return;
        }
        if (!formData.username.trim()) {
          toast.error('Username is required');
          setLoading(false);
          return;
        }
        if (!formData.phone.trim()) {
          toast.error('Phone number is required');
          setLoading(false);
          return;
        }
        if (!formData.email.trim()) {
          toast.error('Email is required');
          setLoading(false);
          return;
        }
        if (!formData.password) {
          toast.error('Password is required');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirm_password) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!formData.agreed_to_terms) {
          toast.error('You must accept the Terms & Conditions and Privacy Policy');
          setLoading(false);
          return;
        }
        
        const user = await register({
          ...formData,
          role,
        });
        
        toast.success('Account created successfully!');
        navigate(`/${user.role}`);
      }
    } catch (error) {
      // Handle validation errors (array format) vs regular errors (string format)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Authentication failed';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || 'Validation error';
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Handlers
  const handleForgotPasswordSubmit = async () => {
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        identifier: forgotPasswordData.identifier,
        email: forgotPasswordData.email,
      });
      toast.success('OTP sent to your email!');
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        email: forgotPasswordData.email,
        otp: forgotPasswordData.otp,
      });
      toast.success('OTP verified! Set your new password.');
      setForgotPasswordStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (forgotPasswordData.new_password !== forgotPasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (forgotPasswordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotPasswordData.email,
        otp: forgotPasswordData.otp,
        new_password: forgotPasswordData.new_password,
        confirm_password: forgotPasswordData.confirm_password,
      });
      toast.success('Password reset successfully! You can now sign in.');
      // Reset state and go back to login
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setForgotPasswordData({
        identifier: '',
        email: '',
        otp: '',
        new_password: '',
        confirm_password: '',
      });
      setIsLogin(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      identifier: '',
      email: '',
      otp: '',
      new_password: '',
      confirm_password: '',
    });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Side - Image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1696798637345-6e58432ca556?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNpdHklMjBzdHJlZXQlMjBuaWdodHxlbnwwfHx8fDE3NjkxNDYxNDR8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative h-full flex flex-col items-center justify-center p-12 text-white">
          <button 
            onClick={() => navigate('/')}
            className="flex flex-col items-center hover:opacity-80 transition-opacity"
            data-testid="logo-home-btn-desktop"
          >
            <img src="/logo.png" alt="vgoride" className="w-20 h-20 object-contain mb-6" />
            <h2 className="font-outfit font-bold text-4xl mb-4">Welcome to vgoride</h2>
          </button>
          <p className="text-lg text-white/80 text-center max-w-md">
            Your journey starts here. Fast, reliable rides at your fingertips.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="bg-dark-bg flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          {/* Logo for mobile - Clickable to go back to landing */}
          <button 
            onClick={() => navigate('/')}
            className="lg:hidden flex items-center justify-center gap-2 mb-8 mx-auto hover:opacity-80 transition-opacity"
            data-testid="logo-home-btn"
          >
            <img src="/logo.png" alt="vgoride" className="w-12 h-12 object-contain" />
            <span className="text-3xl font-outfit font-bold text-white">vgoride</span>
          </button>

          <div className="glass-panel rounded-2xl p-8">
            <div className="mb-8">
              <h1 className="font-outfit font-bold text-3xl text-white mb-2">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h1>
              <p className="text-white/60">
                {isLogin ? 'Welcome back! Please enter your details.' : 'Join vgoride and start your journey.'}
              </p>
            </div>

            {/* Role Selection (Register only) */}
            {!isLogin && (
              <div className="mb-6">
                <Label className="text-white mb-3 block">I want to</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('rider')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      role === 'rider'
                        ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                        : 'border-white/10 text-white/60 hover:border-white/20'
                    }`}
                    data-testid="role-rider-btn"
                  >
                    <UserPlus className="w-6 h-6 mx-auto mb-2" />
                    <div className="font-medium">Ride</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('driver')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      role === 'driver'
                        ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                        : 'border-white/10 text-white/60 hover:border-white/20'
                    }`}
                    data-testid="role-driver-btn"
                  >
                    <Car className="w-6 h-6 mx-auto mb-2" />
                    <div className="font-medium">Drive</div>
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name" className="text-white mb-2 block">
                        First Name <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          id="first_name"
                          type="text"
                          required
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                          placeholder="John"
                          data-testid="input-first-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="last_name" className="text-white mb-2 block">
                        Last Name <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          id="last_name"
                          type="text"
                          required
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                          placeholder="Doe"
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="username" className="text-white mb-2 block">
                      Username <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        id="username"
                        type="text"
                        required
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="johndoe"
                        data-testid="input-username"
                      />
                    </div>
                    <p className="text-xs text-white/40 mt-1">Only letters, numbers, and underscores allowed.</p>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white mb-2 block">
                      Phone Number <span className="text-red-400">*</span>
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="+1 234 567 8900"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  Email <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                    placeholder="you@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-white mb-2 block">
                  Password <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                    placeholder="••••••••"
                    data-testid="input-password"
                  />
                </div>
                {!isLogin && (
                  <p className="text-xs text-white/40 mt-1">Minimum 6 characters required.</p>
                )}
                {isLogin && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-neon-cyan hover:text-white transition-colors"
                      data-testid="forgot-password-btn"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="confirm_password" className="text-white mb-2 block">
                    Confirm Password <span className="text-red-400">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="confirm_password"
                      type="password"
                      required
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                      placeholder="••••••••"
                      data-testid="input-confirm-password"
                    />
                  </div>
                  {formData.password && formData.confirm_password && formData.password !== formData.confirm_password && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                  {formData.password && formData.confirm_password && formData.password === formData.confirm_password && (
                    <p className="text-xs text-green-400 mt-1">Passwords match ✓</p>
                  )}
                </div>
              )}

              {/* Terms & Conditions Checkbox - Registration Only */}
              {!isLogin && (
                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="agreed_to_terms"
                    checked={formData.agreed_to_terms}
                    onChange={(e) => setFormData({ ...formData, agreed_to_terms: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-dark-subtle text-neon-cyan focus:ring-neon-cyan focus:ring-offset-0 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="agreed_to_terms" className="text-sm text-white/70 cursor-pointer">
                    I agree to the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neon-cyan hover:text-white underline transition-colors"
                      data-testid="terms-link"
                    >
                      Terms & Conditions
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neon-cyan hover:text-white underline transition-colors"
                      data-testid="privacy-link"
                    >
                      Privacy Policy
                    </a>
                    <span className="text-red-400"> *</span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                data-testid="submit-btn"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {/* Social Login Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-dark-surface text-white/50">Or continue with</span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div className="space-y-3">
              {/* Google Sign-In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={socialLoading === 'google'}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 rounded-full font-medium py-3 transition-all disabled:opacity-50"
                data-testid="google-signin-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {socialLoading === 'google' ? 'Connecting...' : 'Continue with Google'}
              </button>

              {/* Facebook Sign-In - Hidden for future use
              <button
                type="button"
                onClick={handleFacebookSignIn}
                disabled={socialLoading === 'facebook'}
                className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-full font-medium py-3 transition-all disabled:opacity-50"
                data-testid="facebook-signin-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                {socialLoading === 'facebook' ? 'Connecting...' : 'Continue with Facebook'}
              </button>
              */}

              {/* Apple Sign-In - Hidden for future use
              <button
                type="button"
                onClick={handleAppleSignIn}
                disabled={socialLoading === 'apple'}
                className="w-full flex items-center justify-center gap-3 bg-black hover:bg-gray-900 text-white rounded-full font-medium py-3 transition-all border border-white/20 disabled:opacity-50"
                data-testid="apple-signin-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {socialLoading === 'apple' ? 'Connecting...' : 'Continue with Apple'}
              </button>
              */}
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-white/60 hover:text-neon-cyan transition-colors"
                data-testid="toggle-mode-btn"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>

            {/* Contact Support */}
            <div className="mt-6 pt-4 border-t border-white/10 text-center">
              <p className="text-white/40 text-sm">Need help?</p>
              <a 
                href="mailto:customer-support@vgoride.net" 
                className="text-neon-cyan hover:text-white transition-colors text-sm flex items-center justify-center gap-2 mt-1"
                data-testid="auth-contact-link"
              >
                <Mail className="w-4 h-4" />
                customer-support@vgoride.net
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50">
          <div className="bg-dark-surface rounded-2xl p-8 w-full max-w-md relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              onClick={resetForgotPassword}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
              data-testid="close-forgot-modal"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Step 1: Enter username/phone and email */}
            {forgotPasswordStep === 1 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-neon-cyan/20 flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Forgot Password?</h2>
                    <p className="text-white/60 text-sm">Enter your details to receive an OTP</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block">Username or Phone Number</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="text"
                        value={forgotPasswordData.identifier}
                        onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, identifier: e.target.value })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="Enter your username or phone"
                        data-testid="forgot-identifier-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="email"
                        value={forgotPasswordData.email}
                        onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, email: e.target.value })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="Enter your email address"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleForgotPasswordSubmit}
                    disabled={forgotPasswordLoading || !forgotPasswordData.identifier || !forgotPasswordData.email}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 mt-6"
                    data-testid="send-otp-btn"
                  >
                    {forgotPasswordLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </div>

                <button
                  onClick={resetForgotPassword}
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mt-6 mx-auto"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </button>
              </>
            )}

            {/* Step 2: Enter OTP */}
            {forgotPasswordStep === 2 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-neon-cyan/20 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Verify OTP</h2>
                    <p className="text-white/60 text-sm">Enter the 6-digit code sent to your email</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-white/80">
                      OTP sent to <span className="text-neon-cyan font-medium">{forgotPasswordData.email}</span>
                    </p>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Enter OTP</Label>
                    <Input
                      type="text"
                      value={forgotPasswordData.otp}
                      onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-14 text-white text-center text-2xl tracking-[0.5em] font-mono"
                      placeholder="000000"
                      maxLength={6}
                      data-testid="otp-input"
                    />
                    <p className="text-xs text-white/40 mt-2 text-center">OTP expires in 10 minutes</p>
                  </div>

                  <button
                    onClick={handleVerifyOTP}
                    disabled={forgotPasswordLoading || forgotPasswordData.otp.length !== 6}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 mt-6"
                    data-testid="verify-otp-btn"
                  >
                    {forgotPasswordLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>

                  <button
                    onClick={() => setForgotPasswordStep(1)}
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mt-4 mx-auto"
                    data-testid="resend-otp-btn"
                  >
                    Did not receive OTP? Go back to resend
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Reset Password */}
            {forgotPasswordStep === 3 && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Reset Password</h2>
                    <p className="text-white/60 text-sm">Create your new password</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="password"
                        value={forgotPasswordData.new_password}
                        onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, new_password: e.target.value })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="Enter new password"
                        data-testid="new-password-input"
                      />
                    </div>
                    <p className="text-xs text-white/40 mt-1">Minimum 6 characters required</p>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="password"
                        value={forgotPasswordData.confirm_password}
                        onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, confirm_password: e.target.value })}
                        className="bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl h-12 text-white pl-10"
                        placeholder="Confirm new password"
                        data-testid="confirm-new-password-input"
                      />
                    </div>
                    {forgotPasswordData.new_password && forgotPasswordData.confirm_password && (
                      forgotPasswordData.new_password === forgotPasswordData.confirm_password ? (
                        <p className="text-xs text-green-400 mt-1">Passwords match ✓</p>
                      ) : (
                        <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                      )
                    )}
                  </div>

                  <button
                    onClick={handleResetPassword}
                    disabled={forgotPasswordLoading || !forgotPasswordData.new_password || forgotPasswordData.new_password !== forgotPasswordData.confirm_password}
                    className="w-full bg-green-500 text-white hover:bg-green-600 rounded-full font-bold py-4 shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 mt-6"
                    data-testid="reset-password-btn"
                  >
                    {forgotPasswordLoading ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}